/**
 * Registry of the Akita stores persisted on IDB and of their realtime V2 entity names.
 *
 * Stores self-register through the `@PersistedStoreConfig` decorator (see
 * persisted-store-config.ts) instead of being listed in a central file: adding an entity must not
 * require editing any shared list (open/closed; those lists were a constant source of merge
 * conflicts). The registry is pure — it never touches storage by itself. Registrations are queued
 * until `startPersistEngine()` (localforage.settings.ts) provides the persistState factory:
 *
 * - Stores in the eager bundle register at module evaluation, BEFORE main.ts runs its body, so
 *   their persistState is created (and counted by the hydration gate) before bootstrap.
 * - Stores registered later (lazy chunks, runtime) get their persistState created immediately on
 *   registration. That is safe for hydration only while the IDB snapshot is in memory: getItem then
 *   answers synchronously and persistState subscribes to $$addStore before the store is
 *   constructed — the same contract as MetaInterfaceServiceProvider.loadStores.
 *
 * The registry also owns the realtime V2 entity <-> store name mapping (formerly the literal map in
 * realtime-v2-entity-name-map.ts, which stays as a facade over these methods).
 */

export interface PersistedStoreRegistration {
  /** Akita store name. It is also the IDB key — must stay stable across releases. */
  name: string;
  /** Canonical realtime V2 protocol name (used in both directions: entity <-> store). */
  realtimeEntity?: string;
  /** Additional accepted protocol names (entity -> store direction only). */
  realtimeEntityAliases?: string[];
  /** Also persist the Akita UI store child (`UI/<name>`, created via createUIStore). */
  persistUiStore?: boolean;
  /**
   * 'external': the store is persisted by a dedicated persistState with custom hooks (see
   * CONFIGURED_STORES in localforage.settings.ts) or by another mechanism; the registry records it
   * as persisted but does not create a standard persistState for it.
   */
  persistence?: 'standard' | 'external';
}

export type CreatePersistState = (key: string) => unknown;

export class PersistRegistry {
  private registrations = new Map<string, PersistedStoreRegistration>();
  private duplicates: string[] = [];
  /** Every storage key covered by a persistState (standard, UI/<name>, or external). */
  private persistedKeys = new Set<string>();
  /** Standard keys registered before the engine started, waiting for their persistState. */
  private pendingKeys: string[] = [];
  private instances: unknown[] = [];
  private createPersistState: CreatePersistState | null = null;
  private isSnapshotLoaded: (() => boolean) | null = null;
  private entityToStore = new Map<string, string>();
  private storeToEntity = new Map<string, string>();

  register(registration: PersistedStoreRegistration): void {
    if (this.registrations.has(registration.name)) {
      this.duplicates.push(registration.name);
      console.error(`[PERSIST REGISTRY] store '${registration.name}' registrato due volte: registrazione ignorata`);
      return;
    }
    this.registrations.set(registration.name, registration);
    this.persistedKeys.add(registration.name);

    if (registration.realtimeEntity) {
      this.entityToStore.set(registration.realtimeEntity, registration.name);
      this.storeToEntity.set(registration.name, registration.realtimeEntity);
      for (const alias of registration.realtimeEntityAliases || []) {
        this.entityToStore.set(alias, registration.name);
      }
    }

    const standardKeys: string[] = [];
    if (registration.persistence !== 'external') {
      standardKeys.push(registration.name);
    }
    if (registration.persistUiStore) {
      const uiKey = `UI/${registration.name}`;
      standardKeys.push(uiKey);
      this.persistedKeys.add(uiKey);
    }

    if (this.createPersistState) {
      // Late registration (lazy chunk / runtime): hydration is deterministic only on the
      // synchronous-snapshot path — same warning contract as MetaInterfaceServiceProvider.
      if (standardKeys.length && this.isSnapshotLoaded && !this.isSnapshotLoaded()) {
        console.warn('[PERSIST REGISTRY] snapshot IDB non in memoria: persistenza non garantita per', standardKeys);
      }
      for (const key of standardKeys) {
        this.instances.push(this.createPersistState(key));
      }
    } else {
      this.pendingKeys.push(...standardKeys);
    }
  }

  /**
   * How many standard persistStates a subsequent start() will create. The hydration gate must read
   * this BEFORE calling start() to size its take(N) on selectPersistStateInit.
   */
  pendingKeyCount(): number {
    return this.pendingKeys.length;
  }

  /** Provides the persistState factory and creates the queued persistStates. Called once. */
  start(createPersistState: CreatePersistState, isSnapshotLoaded: () => boolean): void {
    if (this.createPersistState) {
      console.error('[PERSIST REGISTRY] start() chiamato due volte: ignorato');
      return;
    }
    this.createPersistState = createPersistState;
    this.isSnapshotLoaded = isSnapshotLoaded;
    const queued = this.pendingKeys;
    this.pendingKeys = [];
    for (const key of queued) {
      this.instances.push(createPersistState(key));
    }
  }

  /** true if the store (or UI child) is covered by a persistState — static or external. */
  isPersistedStore(storeName: string): boolean {
    return this.persistedKeys.has(storeName);
  }

  /** Realtime V2 entity name -> Akita store name; identity when unmapped (protocol contract). */
  entityToStoreName(entityName: string): string {
    return this.entityToStore.get(entityName) || entityName;
  }

  /** Akita store name -> canonical realtime V2 entity name; identity when unmapped. */
  storeNameToEntityName(storeName: string): string {
    return this.storeToEntity.get(storeName) || storeName;
  }

  /** Unique names of the Akita stores fed by the realtime V2 entity mapping. */
  realtimeFedStoreNames(): readonly string[] {
    return Array.from(new Set(this.entityToStore.values()));
  }

  registeredStoreNames(): readonly string[] {
    return Array.from(this.registrations.keys());
  }

  duplicateRegistrations(): readonly string[] {
    return [...this.duplicates];
  }

  persistStateInstances(): readonly unknown[] {
    return [...this.instances];
  }
}

/** App-wide singleton: the decorator and the persist engine both operate on this instance. */
export const persistRegistry = new PersistRegistry();

export const registerPersistedStore = (registration: PersistedStoreRegistration): void =>
  persistRegistry.register(registration);

export const isRegisteredPersistedStore = (storeName: string): boolean =>
  persistRegistry.isPersistedStore(storeName);

export const realtimeEntityToStoreName = (entityName: string): string =>
  persistRegistry.entityToStoreName(entityName);

export const storeNameToRealtimeEntity = (storeName: string): string =>
  persistRegistry.storeNameToEntityName(storeName);

export const getRealtimeFedStoreNames = (): readonly string[] =>
  persistRegistry.realtimeFedStoreNames();
